/**
 * Custom hook for making API requests to the server using methods like GET, POST, PUT, and DELETE.
 * It handles loading states, error states, and stores the data from the latest successful request.
 * The base URL for the API is taken from the `REACT_APP_SERVER_API_URL` environment variable.
 * Authentication headers are automatically included via the `useAuth` hook.
 *
 * @template T - The expected type of the data in the API response for GET requests.
 *             For POST, PUT, DELETE, this can be the type of the expected response data, or `any`.
 *
 * @returns {UseServerApiReturn} An array containing:
 *          1. `methods` (object): An object with async functions:
 *             - `get<T = any>(relativeUrl: string): Promise<ApiResponse<T>>`
 *             - `post<T = any>(relativeUrl: string, payload: any): Promise<ApiResponse<T>>`
 *             - `put<T = any>(relativeUrl: string, payload: any): Promise<ApiResponse<T>>`
 *             - `delete<T = any>(relativeUrl: string): Promise<ApiResponse<T>>`
 *             Each method returns a Promise resolving to an `ApiResponse` tuple: `[boolean (success), T | null (data), any | null (error)]`.
 *          2. `data` (any | null): The data from the last successful API request. Typed based on the last call if generics are used appropriately by the caller.
 *          3. `error` (any | null): The error object or message from the last failed API request.
 *          4. `loading` (boolean): A boolean indicating if an API request is currently in progress.
 *
 * @example
 * import useServerApi from './useServerApi'; // Adjust path as necessary
 *
 * const MyComponent = () => {
 *   const [{ get, post, put, delete: deleteApi }, data, error, loading] = useServerApi();
 *
 *   const fetchItems = async () => {
 *     const [success, items, fetchError] = await get('/items');
 *     if (success) {
 *       console.log('Fetched items:', items);
 *       // items are also available in the 'data' variable from the hook
 *     } else {
 *       console.error('Error fetching items:', fetchError);
 *       // fetchError is also available in the 'error' variable from the hook
 *     }
 *   };
 *
 *   const addNewItem = async (newItemData) => {
 *     const [success, createdItem, createError] = await post('/items', newItemData);
 *     if (success) {
 *       console.log('Item created:', createdItem);
 *     } else {
 *       console.error('Error creating item:', createError);
 *     }
 *   };
 *
 *   // Call fetchItems() or addNewItem() when needed, e.g., in useEffect or event handlers
 *
 *   if (loading) return <p>Loading...</p>;
 *   // Note: 'error' from the hook reflects the error of the *last* operation.
 *   // The error returned by individual get/post/put/delete calls is specific to that call.
 *   if (error) return <p>An error occurred: {error.message || JSON.stringify(error)}</p>;
 *
 *   return (
 *     <div>
 *       {* Render your component data, potentially using 'data' from the hook *}
 *       <button onClick={fetchItems}>Fetch Items</button>
 *       <button onClick={() => addNewItem({ name: 'New Test Item' })}>Add Item</button>
 *     </div>
 *   );
 * };
 */
import { useState, useCallback } from 'react';
import { getBackendBaseUrl } from '../utils';
// Assuming useAuth is in '../provider' as per useFetch.ts. Adjust if necessary.
import { useAuth } from '../provider';

// Define a generic type for the expected return structure of API methods
type ApiResponse<T = any> = [boolean, T | null, any | null]; // [success, data, error]

// Define the structure of the methods object that will be returned
interface ApiMethods {
    get: <T = any>(relativeUrl: string) => Promise<ApiResponse<T>>;
    post: <T = any>(relativeUrl: string, payload: any) => Promise<ApiResponse<T>>;
    put: <T = any>(relativeUrl: string, payload: any) => Promise<ApiResponse<T>>;
    delete: <T = any>(relativeUrl: string) => Promise<ApiResponse<T>>; // Renamed to 'deleteMethod' internally if 'delete' causes issues
}

// Define the overall return type of the hook
type UseServerApiReturn = [ApiMethods, any | null, any | null, boolean];

const useServerApi = (): UseServerApiReturn => {
    const [data, setData] = useState<any | null>(null);
    const [error, setError] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const { getAuthHeaders } = useAuth(); // For fetching auth tokens

    const BASE_URL = getBackendBaseUrl();

    const get = useCallback(async <T = any>(relativeUrl: string): Promise<ApiResponse<T>> => {
        setLoading(true);
        setError(null);
        const authHeaders = getAuthHeaders(); // Get fresh headers per request
        const fullUrl = `${BASE_URL}${relativeUrl}`;

        try {
            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                // Attempt to parse error body, otherwise use statusText
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = response.statusText;
                }
                throw errorData; // This will be caught by the catch block
            }

            const responseData: T = await response.json();
            setData(responseData); // Update hook's data state
            return [true, responseData, null];
        } catch (err: any) {
            setError(err); // Update hook's error state
            // If err has a message property, prefer that, otherwise serialize err.
            // For simplicity, returning the err object itself, or err.message if it exists.
            const errorToReturn = err.message ? new Error(err.message) : err;
            return [false, null, errorToReturn];
        } finally {
            setLoading(false);
        }
    }, [BASE_URL, getAuthHeaders]);

    const post = useCallback(async <T = any>(relativeUrl: string, payload: any): Promise<ApiResponse<T>> => {
        setLoading(true);
        setError(null);
        const authHeaders = getAuthHeaders();
        const fullUrl = `${BASE_URL}${relativeUrl}`;

        try {
            const isFormData = payload instanceof FormData;
            const fetchOptions: RequestInit = {
                method: 'POST',
                headers: authHeaders,
            };

            if (isFormData) {
                fetchOptions.body = payload;
            } else {
                fetchOptions.headers = {
                    ...fetchOptions.headers,
                    'Content-Type': 'application/json',
                };
                fetchOptions.body = JSON.stringify(payload);
            }

            const response = await fetch(fullUrl, fetchOptions);

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = response.statusText;
                }
                throw errorData;
            }

            const responseData: T = await response.json();
            setData(responseData); // Update hook's data state
            return [true, responseData, null];
        } catch (err: any) {
            setError(err); // Update hook's error state
            const errorToReturn = err.message ? new Error(err.message) : err;
            return [false, null, errorToReturn];
        } finally {
            setLoading(false);
        }
    }, [BASE_URL, getAuthHeaders]);

    const put = useCallback(async <T = any>(relativeUrl: string, payload: any): Promise<ApiResponse<T>> => {
        setLoading(true);
        setError(null);
        const authHeaders = getAuthHeaders();
        const fullUrl = `${BASE_URL}${relativeUrl}`;

        try {
            const response = await fetch(fullUrl, {
                method: 'PUT',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = response.statusText;
                }
                throw errorData;
            }

            const responseData: T = await response.json();
            setData(responseData); // Update hook's data state
            return [true, responseData, null];
        } catch (err: any) {
            setError(err); // Update hook's error state
            const errorToReturn = err.message ? new Error(err.message) : err;
            return [false, null, errorToReturn];
        } finally {
            setLoading(false);
        }
    }, [BASE_URL, getAuthHeaders]);

    const deleteMethod = useCallback(async <T = any>(relativeUrl: string): Promise<ApiResponse<T>> => {
        setLoading(true);
        setError(null);
        const authHeaders = getAuthHeaders();
        const fullUrl = `${BASE_URL}${relativeUrl}`;

        try {
            const response = await fetch(fullUrl, {
                method: 'DELETE',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = response.statusText;
                }
                throw errorData;
            }

            let responseData: T | null = null;
            if (response.status !== 204) { // No content to parse for 204
                try {
                    responseData = await response.json();
                } catch (e) {
                    // If parsing fails for a 2xx (non-204) response, treat as null or handle as error
                    // For now, sticking to null if parsing fails but response was ok.
                }
            }

            setData(responseData);
            return [true, responseData, null];
        } catch (err: any) {
            setError(err);
            const errorToReturn = err.message ? new Error(err.message) : err;
            return [false, null, errorToReturn];
        } finally {
            setLoading(false);
        }
    }, [BASE_URL, getAuthHeaders]);

    const methods: ApiMethods = {
        get,
        post,
        put,
        delete: deleteMethod,
    };

    return [methods, data, error, loading];
};

export default useServerApi;